import React from 'react';
import { Box, Button, Checkbox, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export type Key = string | number;

export interface TreeNode<T extends Key = Key> {
  id: T;
  label: React.ReactNode;
  children?: TreeNode<T>[];
}

export interface TransferTreeListProps<T extends Key = Key> {
  items: TreeNode<T>[];
  value: T[]; // keys on the right side
  onChange: (next: T[]) => void;
  leftTitle?: React.ReactNode;
  rightTitle?: React.ReactNode;
  tree?: boolean; // when true, selecting a parent selects all descendants
  height?: number; // px
}

function flattenIds<T extends Key>(nodes: TreeNode<T>[]): T[] {
  const out: T[] = [];
  const walk = (arr: TreeNode<T>[]) => {
    for (const n of arr) {
      out.push(n.id);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

function collectSubtreeIds<T extends Key>(node: TreeNode<T>): T[] {
  const out: T[] = [node.id];
  if (node.children?.length) {
    for (const c of node.children) out.push(...collectSubtreeIds(c));
  }
  return out;
}

function buildIdMap<T extends Key>(nodes: TreeNode<T>[]): Map<T, TreeNode<T>> {
  const m = new Map<T, TreeNode<T>>();
  const walk = (arr: TreeNode<T>[]) => {
    for (const n of arr) {
      m.set(n.id, n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return m;
}

function buildParentMap<T extends Key>(nodes: TreeNode<T>[]): Map<T, T | null> {
  const p = new Map<T, T | null>();
  const walk = (arr: TreeNode<T>[], parent: T | null) => {
    for (const n of arr) {
      p.set(n.id, parent);
      if (n.children?.length) walk(n.children, n.id);
    }
  };
  walk(nodes, null);
  return p;
}

function filterTreeBySet<T extends Key>(nodes: TreeNode<T>[], allowed: Set<T>): TreeNode<T>[] {
  const recur = (arr: TreeNode<T>[]): TreeNode<T>[] => {
    const out: TreeNode<T>[] = [];
    for (const n of arr) {
      const filteredChildren = n.children ? recur(n.children) : [];
      if (allowed.has(n.id) || filteredChildren.length > 0) {
        out.push({ ...n, children: filteredChildren });
      }
    }
    return out;
  };
  return recur(nodes);
}

function flattenTree<T extends Key>(nodes: TreeNode<T>[]): T[] {
  const out: T[] = [];
  const walk = (arr: TreeNode<T>[]) => {
    for (const n of arr) {
      out.push(n.id);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

export function TransferTreeList<T extends Key = Key>(props: TransferTreeListProps<T>) {
  const { items, value, onChange, leftTitle = 'Available', rightTitle = 'Selected', tree = false, height = 300 } = props;

  const allIds = React.useMemo(() => flattenIds(items), [items]);
  const idMap = React.useMemo(() => buildIdMap(items), [items]);
  const parentMap = React.useMemo(() => buildParentMap(items), [items]);
  const rootIds = React.useMemo(() => items.map(i => i.id), [items]);

  const rightSet = React.useMemo(() => new Set<T>(value), [value]);
  const leftIds = React.useMemo(() => allIds.filter(id => !rightSet.has(id)), [allIds, rightSet]);

  const [leftChecked, setLeftChecked] = React.useState<T[]>([]);
  const [rightChecked, setRightChecked] = React.useState<T[]>([]);
  const [expanded, setExpanded] = React.useState<Set<T>>(new Set());

  React.useEffect(() => {
    // reset checks when data/value changes to avoid stale selections
    setLeftChecked([]);
    setRightChecked([]);
    // initialize expanded to root nodes for better UX
    setExpanded(new Set(rootIds));
  }, [items, value, rootIds]);

  const toggleLeft = (id: T) => () => {
    if (!tree) {
      setLeftChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
      return;
    }
    // tree mode: include/exclude entire subtree
    const node = idMap.get(id);
    if (!node) return;
    const ids = collectSubtreeIds(node);
    setLeftChecked(prev => {
      const set = new Set(prev);
      const allIncluded = ids.every(k => set.has(k));
      if (allIncluded) {
        ids.forEach(k => set.delete(k));
      } else {
        ids.forEach(k => set.add(k));
      }
      return Array.from(set);
    });
  };
  const toggleRight = (id: T) => () => {
    if (!tree) {
      setRightChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
      return;
    }
    const node = idMap.get(id);
    if (!node) return;
    const ids = collectSubtreeIds(node);
    setRightChecked(prev => {
      const set = new Set(prev);
      const allIncluded = ids.every(k => set.has(k));
      if (allIncluded) {
        ids.forEach(k => set.delete(k));
      } else {
        ids.forEach(k => set.add(k));
      }
      return Array.from(set);
    });
  };

  const moveToRight = (ids: T[]) => {
    let addSet = new Set<T>(ids);
    if (tree) {
      // include descendants
      const expanded = new Set<T>();
      for (const id of ids) {
        const node = idMap.get(id);
        if (node) collectSubtreeIds(node).forEach(k => expanded.add(k));
        else expanded.add(id);
      }
      // promote parents if all descendants included
      const selection = expanded;
      const checkAllDesc = (node: TreeNode<T>): boolean => {
        const sub = collectSubtreeIds(node);
        return sub.every(k => selection.has(k));
      };
      const walk = (n: TreeNode<T>): void => {
        if (checkAllDesc(n)) selection.add(n.id);
        n.children?.forEach(walk);
      };
      items.forEach(walk);
      addSet = selection;
    }
    const next = Array.from(new Set<T>([...value, ...Array.from(addSet)]));
    onChange(next);
    setLeftChecked([]);
  };

  const moveToLeft = (ids: T[]) => {
    let removeSet = new Set<T>(ids);
    if (tree) {
      const expanded = new Set<T>();
      for (const id of ids) {
        const node = idMap.get(id);
        if (node) collectSubtreeIds(node).forEach(k => expanded.add(k));
        else expanded.add(id);
      }
      removeSet = expanded;
    }
    const next = value.filter(k => !removeSet.has(k));
    onChange(next);
    setRightChecked([]);
  };

  const moveAllRight = () => onChange(allIds);
  const moveAllLeft = () => onChange([]);

  const leftSelected = leftChecked.length > 0;
  const rightSelected = rightChecked.length > 0;

  const nodeCheckState = (sel: Set<T>, node: TreeNode<T>) => {
    if (!tree) {
      const checked = sel.has(node.id);
      return { checked, indeterminate: false };
    }
    const subIds = collectSubtreeIds(node);
    const selectedCount = subIds.filter(id => sel.has(id)).length;
    const all = selectedCount === subIds.length;
    const some = selectedCount > 0 && !all;
    return { checked: all, indeterminate: some };
  };

  const renderTree = (nodes: TreeNode<T>[], checked: Set<T>, onToggle: (id: T) => () => void, level = 0) => (
    <List dense component="div" role="list" disablePadding>
      {nodes.map(node => (
        <React.Fragment key={String(node.id)}>
          <ListItem role="listitem" disablePadding sx={{ pl: level * 2 }}>
            {node.children && node.children.length > 0 ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(prev => {
                    const next = new Set(prev);
                    if (next.has(node.id)) next.delete(node.id); else next.add(node.id);
                    return next;
                  });
                }}
              >
                {expanded.has(node.id) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 32 }} />
            )}
            <ListItemButton onClick={onToggle(node.id)} sx={{ pl: 0 }}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={nodeCheckState(checked, node).checked}
                  indeterminate={nodeCheckState(checked, node).indeterminate}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': String(node.id) }}
                />
              </ListItemIcon>
              <ListItemText id={String(node.id)} primary={node.label ?? String(node.id)} />
            </ListItemButton>
          </ListItem>
          {node.children && node.children.length > 0 && expanded.has(node.id) && renderTree(node.children, checked, onToggle, level + 1)}
        </React.Fragment>
      ))}
    </List>
  );

  const renderList = (ids: T[], checked: T[], onToggle: (id: T) => () => void) => {
    const checkedSet = new Set<T>(checked);
    if (tree) {
      // show hierarchical structure filtered to ids
      const allowed = new Set<T>(ids);
      const filteredTree = filterTreeBySet(items, allowed);
      return (
        <Paper sx={{ width: '100%', height, overflow: 'auto' }}>
          {renderTree(filteredTree, checkedSet, onToggle)}
        </Paper>
      );
    }
    return (
      <Paper sx={{ width: '100%', height, overflow: 'auto' }}>
        <List dense component="div" role="list">
          {ids.map((id) => (
            <ListItem key={String(id)} role="listitem" disablePadding>
              <ListItemButton onClick={onToggle(id)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checked.includes(id)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': String(id) }}
                  />
                </ListItemIcon>
                <ListItemText id={String(id)} primary={idMap.get(id)?.label ?? String(id)} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{leftTitle}</Typography>
          {renderList(leftIds, leftChecked, toggleLeft)}
        </Box>

        <Stack spacing={1} alignItems="center" justifyContent="center">
          <Button variant="outlined" size="small" onClick={() => moveToRight(leftChecked)} disabled={!leftSelected}>
            &gt;
          </Button>
          <Button variant="outlined" size="small" onClick={() => moveToLeft(rightChecked)} disabled={!rightSelected}>
            &lt;
          </Button>
          <Divider flexItem sx={{ my: 1 }} />
          <Button variant="outlined" size="small" onClick={moveAllRight} disabled={leftIds.length === 0}>
            &gt;&gt;
          </Button>
          <Button variant="outlined" size="small" onClick={moveAllLeft} disabled={value.length === 0}>
            &lt;&lt;
          </Button>
        </Stack>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{rightTitle}</Typography>
          {renderList(value, rightChecked, toggleRight)}
        </Box>
      </Stack>
    </Box>
  );
};
